/** Weekly attendance and allocations of one employee (profile page). */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Tag } from "@primereact/ui/tag";
import { apiGet } from "../../lib/api.js";
import { Detail, ErrorPanel, LoadingPanel } from "../shared.js";

export function EmployeeWorkDetailsPanel({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["employee-work-details", userId],
    queryFn: () =>
      apiGet<{
        data: {
          rows: Array<{
            plannedMinutes: number;
            netAttendanceMinutes: number;
            allocatedMinutes: number;
            days: Array<{
              date: string;
              projects: Array<{
                number: string;
                title: string;
                customerName: string;
                minutes: number;
              }>;
            }>;
          }>;
        };
      }>("/time/management", { query: { userId, status: "all" } }),
  });
  const row = query.data?.data.rows[0];
  return (
    <section className="rounded-2xl bg-app-surface p-5 shadow-sm ring-1 ring-app-border">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-app-text">
          {t("employees.workDetails", { defaultValue: "Work details" })}
        </h2>
        <p className="text-sm text-app-muted">
          {t("employees.workDetailsDescription", {
            defaultValue: "Attendance and project allocation for this week.",
          })}
        </p>
      </div>
      {query.isLoading ? (
        <LoadingPanel />
      ) : query.error ? (
        <ErrorPanel error={query.error} />
      ) : row ? (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Detail label="Planned" value={`${row.plannedMinutes} min`} />
            <Detail
              label="Attendance"
              value={`${row.netAttendanceMinutes} min`}
            />
            <Detail label="Allocated" value={`${row.allocatedMinutes} min`} />
          </div>
          <div className="mt-4 space-y-3">
            {row.days.flatMap((day) =>
              day.projects.map((project) => (
                <div
                  key={`${day.date}-${project.number}`}
                  className="flex items-center justify-between gap-3 rounded-md border border-app-border p-3"
                >
                  <div>
                    <strong>{project.number}</strong>
                    <p className="text-sm text-app-muted">
                      {project.title} · {project.customerName}
                    </p>
                  </div>
                  <Tag>{`${project.minutes} min`}</Tag>
                </div>
              )),
            )}
          </div>
        </>
      ) : (
        <p className="text-sm text-app-muted">No work details.</p>
      )}
    </section>
  );
}
